import { cva } from 'class-variance-authority';
import type { ComponentProps, JSX, ReactNode, Ref } from 'react';

export const typographyVariants = cva('', {
    defaultVariants: {
        variant: 'div',
        size: 'default',
    },
    variants: {
        center: {
            true: 'text-center!',
        },
        size: {
            default: '',
            big: '',
        },
        color: {
            primary: 'text-text-dark',
            secondary: 'text-text-secondary',
            grey: 'text-text-grey',
            subscription: 'text-text-subscription',
            link: 'text-text-link',
            blockquote: 'text-text-blockquote',
            light: 'text-text-light',
            'accent-cold': 'text-text-accent-cold',
            'accent-warm': 'text-text-accent-warm',
            white: 'text-white!',
        },
        variant: {
            h1: 'h1!',
            h2: 'h2!',
            h3: 'h3!',
            h4: '',
            h5: '',
            h6: '',
            p: 'font-normal',
            aside: [
                'm-0!',
                'font-normal text-lg leading-[1.5]!',
                'mobile:text-xl',
                'lg-tablet:text-2xl',
            ],
            span: '',
            div: 'leading-[1.2]',
        },
    },
    compoundVariants: [
        {
            variant: 'p',
            size: 'default',
            class: 'base-smaller-text',
        },
        {
            variant: 'p',
            size: 'big',
            class: 'base-text',
        },
    ],
});

type TypographyProps = {
    center?: boolean;
    children: ReactNode;
    className?: string;
    color?:
        | 'primary'
        | 'secondary'
        | 'grey'
        | 'subscription'
        | 'link'
        | 'blockquote'
        | 'light'
        | 'accent-cold'
        | 'accent-warm'
        | 'white';
    ref?: Ref<HTMLDivElement>;
    size?: 'default' | 'big';
    variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'aside' | 'div';
} & ComponentProps<'div'>;

export function Typography({
    center,
    children,
    className = '',
    color = 'primary',
    ref,
    size = 'default',
    variant = 'div',
    ...props
}: TypographyProps) {
    const Component: keyof JSX.IntrinsicElements = variant;

    return (
        <Component
            ref={ref}
            {...props}
            className={typographyVariants({ center, className, color, size, variant })}
        >
            {children}
        </Component>
    );
}

Typography.displayName = 'Typography';
